import {
  CATEGORY_TO_PARTS,
  getMiraUiLabelTranslationKey,
  type CarPartDetailData,
  type PartOperation,
} from '@mira/ui';

import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {isNil} from 'ramda';

import type {TransformedPredictionData} from '../schemas';

function formatPartName(partId: string): string {
  return partId
    .replace(/-interior$/, '')
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Derives CarPartDetailData for a clicked part from already-transformed prediction data.
 * Returns null when no partId is selected or the part has no category mapping.
 * Returns an object with an empty operations array when the part exists in a category
 * but predictions are not yet available — this keeps the modal openable for any
 * colored diagram part regardless of data-loading timing.
 */
export function usePartDetailData(
  partId: string | null,
  transformedData: TransformedPredictionData
): CarPartDetailData | null {
  const {t} = useTranslation();

  return useMemo(() => {
    if (isNil(partId)) {
      return null;
    }

    // Collect all category IDs that include this part
    const categoryIds = new Set<string>();
    for (const [categoryId, mapping] of Object.entries(CATEGORY_TO_PARTS)) {
      if (mapping.parts.includes(partId)) {
        categoryIds.add(categoryId);
      }
    }

    if (categoryIds.size === 0) {
      return null;
    }

    // Walk the transformed tree to find matching category predictions
    const operations: PartOperation[] = [];
    for (const mainGroup of transformedData.mainGroups) {
      for (const sub of mainGroup.subcategories) {
        for (const cat of sub.categories) {
          if (categoryIds.has(cat.categoryKey)) {
            operations.push({
              description: cat.operationDescription,
              variant: cat.rowType,
              percentage: cat.percentage,
              willNeed: cat.will_need,
              label: cat.label,
            });
          }
        }
      }
    }

    const partName = formatPartName(partId);
    const partNameKey = getMiraUiLabelTranslationKey(partName);

    return {
      partId,
      partName: isNil(partNameKey) ? partName : t(partNameKey),
      operations,
    };
  }, [partId, t, transformedData]);
}
